import {useTranslation} from 'react-i18next'
import {Popover} from '@/components/ui/popover/Popover'
import {useEffect, useMemo, useState} from 'react'
import {StyledEmptySection, StyledHostSelectContainer, StyledHostSelectRoot} from './style'
import {ChatButton} from '../chat-button/ChatButton'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {LocalStorageManager} from '@/utilities/localStorage'
import {useRootStore} from '@/store'
import {selectChatStore} from '@/store/selectors'
import {useGetRepresentedUser} from '@/hooks/useGetRepresentedHost'
import {useNavigate} from 'react-router-dom'
import {ROUTE_MESSAGE_CENTER} from '@/utilities/constants/routeNames'
import {useGetRepresentativeHostsChannelsV3} from '@/features/representative/queries/useGetRepresentativeHostsChannelsV3'
import {ChatHostOption} from './ChatHostOption'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/features/representative/queries/keys.ts'
import {useMe} from '@/features/authentication/queries/useMe'
import {UserRoleE} from '@/types'
import {useGetUnreadMessageCountRepsV3} from '@/features/chat/queries/useGetUnreadMessageCountRepsV3'
import {QUERY_KEYS as QUERY_KEYS_CHAT} from '@/features/chat/queries/keys.ts'

type ChatHostsSelectProps = {
    isLight: boolean
    isAgent: boolean
    getCountOfSelectedUser: (num: number) => void
}

export const ChatHostsSelect = ({isLight, isAgent, getCountOfSelectedUser}: ChatHostsSelectProps) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const {data: user} = useMe()
    const {data: hostsUnreadMessageCountReps} = useGetUnreadMessageCountRepsV3()
    const {setNeedReInitAfterChangedRepresentedHost} = useRootStore(selectChatStore)
    const {isLoading, agentAndRepsHosts: hosts, representedUserId} = useGetRepresentedUser()
    const [isOpen, setIsOpen] = useState(false)
    const [selectedHostId, setSelectedHostId] = useState<number | undefined>(representedUserId)
    const queryClient = useQueryClient()
    const {data: hotsChannels} = useGetRepresentativeHostsChannelsV3(!isAgent)
    const count = 0

    const correctHosts = useMemo(() => {
        if (isAgent) {
            return hosts
        } else {
            return hosts.length
                ? hosts.map(host => ({
                      ...host,
                      hostChannels: hotsChannels?.[`${host.id}`]
                  }))
                : []
        }
    }, [hotsChannels, hosts])

    useEffect(() => {
        getCountOfSelectedUser(count || 0)
    }, [count])

    const setSelectedHostIdHandler = (hostID: number) => {
        LocalStorageManager.chatUserId.set(hostID)
        setSelectedHostId(hostID)
        setNeedReInitAfterChangedRepresentedHost(true)
    }
    useEffect(() => {
        if (
            (!selectedHostId && correctHosts.length) ||
            (selectedHostId && correctHosts.length && !correctHosts.find(host => host.id === selectedHostId))
        ) {
            setSelectedHostIdHandler(correctHosts[0].rookieId ?? (correctHosts[0].id as number))
            navigate(ROUTE_MESSAGE_CENTER)
        }
    }, [correctHosts])

    useEffect(() => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS_CHAT.getUnreadMessageCountRepresentativeV3]})
    }, [])

    return (
        <StyledHostSelectContainer>
            <Popover
                trigger={<ChatButton isLight={isLight} />}
                rootProps={{
                    open: isOpen,
                    onOpenChange: () => {
                        setIsOpen(!isOpen)
                        !isOpen && queryClient.invalidateQueries({queryKey: [QUERY_KEYS.representativeHostsChannelsV3]})
                    }
                }}
                isModal
            >
                <StyledHostSelectRoot>
                    {isLoading ? (
                        <Spinner />
                    ) : correctHosts?.length ? (
                        correctHosts.map(host => {
                            const currentHostId = user?.type === UserRoleE.Representative ? host?.rookieId : host?.id

                            const matched = hostsUnreadMessageCountReps?.unreadMessagesCount?.find(
                                item => item.creatorId === currentHostId
                            )
                            const count = matched?.unreadMessagesCount ?? 0
                            return (
                                <ChatHostOption
                                    key={host.id}
                                    onClick={() => {
                                        setIsOpen(false)
                                        setSelectedHostIdHandler(currentHostId as number)
                                        navigate(ROUTE_MESSAGE_CENTER)
                                    }}
                                    host={host}
                                    selectedHostId={selectedHostId}
                                    count={isAgent ? 0 : count}
                                />
                            )
                        })
                    ) : (
                        <StyledEmptySection>{t('common:no_options')}</StyledEmptySection>
                    )}
                </StyledHostSelectRoot>
            </Popover>
        </StyledHostSelectContainer>
    )
}
